import { IRuntimeVO } from 'library/ts/interfaces/utilities/runtime/IRuntimeVO';
import { IRuntimeVOApp } from 'library/ts/interfaces/utilities/runtime/IRuntimeVOApp';
import { IRuntimeVODebug } from 'library/ts/interfaces/utilities/runtime/IRuntimeVODebug';
import { IRuntimeVOTop } from 'library/ts/interfaces/utilities/runtime/IRuntimeVOTop';
import { IRuntimeVOURL } from 'library/ts/interfaces/utilities/runtime/IRuntimeVOURL';
import { IProjectInfo } from 'library/ts/IProjectInfo';
import { Country } from 'library/ts/model/enums/geo/Country';
import { Cookies } from 'library/ts/utilities/Cookies';
import { ICookies } from 'library/ts/utilities/interfaces/ICookies';
import { LOGGER_QUERY_PARAMS } from 'library/ts/utilities/logger/LogServiceConstants';
import { Utilities } from 'library/ts/utilities/Utilities';

export abstract class RuntimeVO implements IRuntimeVO {

	readonly #cookieName: string;
	#cookies: ICookies;
	readonly #debugCookie: { debug: boolean | number, debugPlayer: boolean, debugLowLevel: boolean };
	readonly #utils: Utilities;
	#app_name: string;
	#project: IProjectInfo;

	constructor( cookieName: string, app_name: string ) {
		// set cookie name
		this.#cookieName = cookieName;
		// set app name
		this.#app_name = app_name;
		this.#project = window.PlayerServicesProjectInfo;
		// instantiate Utilities
		this.#utils = window?.PARA?.PlayerServices?.Utilities;
		// instantiate cookies library
		this.#cookies = Cookies.getInstance();
		// read cookie
		this.#debugCookie = JSON.parse( this.#cookies.getItem( this.#cookieName ) );
	}

	/**
	 * @description Returns instance of Utilities
	 * @return {Utilities}
	 */
	get utils() {
		return this.#utils;
	}

	/**
	 * @description Information about Application script
	 */
	get app(): IRuntimeVOApp {
		/**
		 * @description Returns Application import URL parsed from page scripts
		 * @return {string}
		 */
		const appURL = (): string => {
			const scripts = document.getElementsByTagName( 'script' );
			for ( let i = 0; i < scripts.length; i++ ) {
				// if script has application file name

				if ( this.utils.String.hasSubstring( scripts[ i ].src, [ this.#project.app_output_file[ this.#app_name ].js.min.value ] ) ) {
					return scripts[ i ].src;
				}
			}
			return undefined;
		};

		const url = appURL();
		const parsed = this.utils.URL.parser( url );
		const ssl = parsed.protocol.indexOf( 'https' ) > -1;
		const root = url.split( this.#project.app_output_file[ this.#app_name ].js.min.value )[ 0 ];

		return {
			url: url,
			root: root,
			parsed: parsed,
			ssl: ssl
		};
	}

	/**
	 * @description Information about page application is embedded at
	 */
	get top(): IRuntimeVOTop {
		const top = this.utils.URL.getTopReferrer();
		const parsed = this.utils.URL.parser( top );
		const ssl = parsed.protocol.indexOf( 'https' ) > -1;
		let country: Country = Country.US;
		if ( this.utils.URL.isPARA() ) country = this.utils.URL.countryFromURL();

		return {
			url: top,
			parsed: parsed,
			ssl: ssl,
			country: country
		};
	}

	/**
	 * @description Returns various Application environment related URLs
	 */
	get url(): IRuntimeVOURL {
		/**
		 * @description Returns Application import URL parsed from page scripts
		 * @return {string}
		 */
		const appURL = (): string => {
			const scripts = document.getElementsByTagName( 'script' );
			for ( let i = 0; i < scripts.length; i++ ) {
				// if script has application file name
				if ( this.utils.String.hasSubstring( scripts[ i ].src, [ this.#project.app_output_file[ this.#app_name ].js.min.value ] ) ) {
					return scripts[ i ].src;
				}
			}
			return undefined;
		};

		/**
		 * @description Returns root URL for all applications
		 * @return {string}
		 */
		const getRootURL = (): string => {
			// split application URL
			const splitURL = appURL().split( this.#project.app_output_dir[ this.#app_name ].value );
			// sanity check
			if ( this.utils.Type.isArray( splitURL ) && !this.utils.Type.isEmpty( splitURL ) ) {
				return splitURL[ 0 ]; // return root URL
			}
			return ''; // otherwise, return empty string
		};

		return {
			/**
			 * @description Returns root URL for all applications
			 */
			root: getRootURL(),
			/**
			 * @description URL of current application
			 */
			app: appURL()
		};
	}

	/**
	 * @description Debug status
	 */
	get debug(): IRuntimeVODebug {
		const topURL = this.top.url;
		const debugQueryParams = [
			+this.utils.URL.getQueryField( LOGGER_QUERY_PARAMS.DEBUG_SMART_TAG, topURL ),
			+this.utils.URL.getQueryField( LOGGER_QUERY_PARAMS.DEBUG, topURL )
		];

		const validLogLevel = ( logLevel: number ) => {
			return !!logLevel && !this.utils.Type.isEmpty( logLevel ) && this.utils.Type.isNumber( +logLevel );
		};
		const findValidLogLevel = ( logLevels: number[] ) => {
			for ( let i = 0, l = logLevels.length; i < l; ++i ) {
				if ( validLogLevel( logLevels[ i ] ) ) {
					return logLevels[ i ];
				}
			}
		};

		// look for the first valid log level
		let logLevel = findValidLogLevel( debugQueryParams );
		if ( !logLevel ) logLevel = 0;

		const output = {
			cacheBust: this.utils.Convert.stringToBoolean( this.utils.URL.getQueryField( 'cacheBust', topURL ), false ),
			logLevel: logLevel,
			on: !!logLevel,
			player: this.utils.Convert.stringToBoolean( this.utils.URL.getQueryField( 'debugPlayer', topURL ), false ),
			lowLevel: this.utils.Convert.stringToBoolean( this.utils.URL.getQueryField( 'debugLowLevel', topURL ), false ),
			debugError: this.utils.URL.getQueryField( 'debugError', topURL )
		};

		// Sample console commands to turn on debugging via Cookie
		// turn on debug for Application and Player for 1 hour, single page
		// Application.cookies.setItem( 'app debug cookie name', JSON.stringify( { debug:true, debugPlayer: true } ), 3600 );
		// turn on debug for Application and Player for 1 hour, persistent across pages
		// Application.cookies.setItem( 'app debug cookie name', JSON.stringify( { debug:true, debugPlayer: true } ), 3600, ';path=/' );
		// turn off debug for Application and Player
		// Application.cookies.setItem( 'app debug cookie name', JSON.stringify( { debug:false, debugPlayer: false } ) );

		// check our debug cookie for persistent debug setup
		if ( !this.utils.Type.isEmpty( this.#debugCookie ) ) {
			let cookieLogLevel = findValidLogLevel( [
				+this.#debugCookie[ LOGGER_QUERY_PARAMS.DEBUG_SMART_TAG ],
				+this.#debugCookie[ LOGGER_QUERY_PARAMS.DEBUG ]
			] );

			if ( !this.utils.Type.isEmpty( cookieLogLevel ) ) {
				if ( this.utils.Type.isNumber( +cookieLogLevel ) ) {
					cookieLogLevel = +cookieLogLevel;
				} else {
					cookieLogLevel = 1;
				}
			} else {
				cookieLogLevel = 0;
			}
			output.logLevel = cookieLogLevel;
			output.on = !!cookieLogLevel;
			output.player = this.#debugCookie.debugPlayer;
			output.lowLevel = this.#debugCookie.debugLowLevel;
		}

		return output;
	}

}